import { useFocusEffect } from '@react-navigation/native';
import { useCallback } from 'react';
import { KeyboardAvoidingView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import {
    BigButton,
    Field,
    Image,
    Keyboard,
    Link,
    Logo,
    Page,
    Platform,
    React,
    Section,
    TextInput
} from '../../common';
import {
    getIsUserFetching,
    getUserError,
    getUserLoadingState
} from '../../selectors';
import { user } from '../../services/user';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isEmailValid = (email) => EMAIL_REGEX.test(email.trim());

export const EmailSignIn = Page.register(
    ({
        email = '',
        isValid = isEmailValid(email),
        setState,
        actions,
        navigation,
        captchaCodeImg,
        captchaCodeNum,
        waitingResponse,
        isCaptchaSubmitted = false,
        loginId,
        isLoading,
        isFetching,
        error,
        onEmail = (text) => {
            setState({
                email: text,
                isValid: isEmailValid(text),
            });
        },
        onCaptchaCode = (code) => {
            setState({ captchaCodeNum: code });
        },
        onSubmitCaptchaCode = async () => {
            setState({ isCaptchaSubmitted: true });
            try {
                const resp = await user.emailCaptchaConfirmation(
                    captchaCodeNum,
                    loginId,
                    (message, errorCode) => {
                        actions.setUserError(message, errorCode);
                        setState({ waitingResponse: false });
                    },
                );
                Keyboard.dismiss();
                if (resp?.id) {
                    setState({ captchaCodeImg: null });
                    actions.setUserError('');
                    Object.trackAction('email_login_captcha_submit');
                    navigation.navigate('CodeConfirmation', {
                        email,
                        id: resp.id,
                        isEmailLogin: true,
                        isSignInWithApi: true,
                    });
                } else if (resp?.body?.attributes?.exceptionClass === 'javax.ws.rs.WebApplicationException') {
                    actions.setUserError(Object.R('error.email_login_unavailable'));
                    navigation.navigate('SignIn');
                }
                setState({ waitingResponse: false });
            } finally {
                setState({ isCaptchaSubmitted: false });
            }
        },
        onSubmitEmail = async () => {
            Object.trackAction('email_login_submit');
            try {
                const result = await user.emailSignIn(
                    email,
                    (message, errorCode) => {
                        if (errorCode === 'UNEXPECTED_PROBLEM') {
                            actions.setUserError(Object.R('error.email_login_unavailable'));
                            navigation.navigate('SignIn');
                        } else {
                            actions.setUserError(message, errorCode);
                        }
                        setState({ waitingResponse: false });
                    },
                );
                if (result?.captchaCode) {
                    setState({
                        captchaCodeImg: result.captchaCode,
                        loginId: result.id,
                        waitingResponse: false,
                    });
                    Object.trackAction('email_login_captcha_shown');
                } else if (result?.id) {
                    setState({ waitingResponse: false });
                    navigation.navigate('CodeConfirmation', {
                        email,
                        id: result.id,
                        isEmailLogin: true,
                        isSignInWithApi: true,
                    });
                } else {
                    setState({ waitingResponse: false });
                }
            } catch {
                setState({ waitingResponse: false });
            }
        },
    }) => {
        const safeArea = useSafeAreaInsets();

        useFocusEffect(
            useCallback(() => {
                actions.setUserError();
            }, []),
        );

        return (
            <Page
                name="email-sign-in"
                persistKeyboard
                notification={
                    error && {
                        ...error,
                        onSubmitEmail,
                    }
                }
                isLoading={isLoading || isFetching}
                safeArea={safeArea}
            >
                <KeyboardAvoidingView
                    behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                    style={{ flex: 1 }}
                >
                    <Section>
                        {captchaCodeImg ? (
                            <Logo.Wrapper title="titles.enter_captcha_code">
                                <Image
                                    source={{ uri: captchaCodeImg }}
                                    style={{
                                        height: 60,
                                        width: 140,
                                        alignSelf: 'center',
                                        marginBottom: 8,
                                    }}
                                />
                                <TextInput.Number
                                    style={{ flex: 1 }}
                                    onChangeText={onCaptchaCode}
                                    value={captchaCodeNum}
                                />
                            </Logo.Wrapper>
                        ) : (
                            <Logo.Wrapper
                                title="titles.enter_your_email"
                                subtitle="titles.email_activation_message"
                            >
                                <Field>
                                    <TextInput
                                        ns="email"
                                        placeholder="placeholders.email"
                                        value={email}
                                        onChangeText={onEmail}
                                        keyboardType="email-address"
                                        autoCapitalize="none"
                                        autoCorrect={false}
                                    />
                                </Field>
                            </Logo.Wrapper>
                        )}
                        <BigButton
                            title="buttons.continue"
                            disabled={
                                captchaCodeImg
                                    ? !captchaCodeNum || isCaptchaSubmitted
                                    : !isValid || waitingResponse
                            }
                            action={
                                waitingResponse
                                    ? () => {}
                                    : () => {
                                        setState({ waitingResponse: true });
                                        (captchaCodeImg ? onSubmitCaptchaCode : onSubmitEmail)();
                                    }
                            }
                        />
                        <Link
                            underline
                            bigger
                            id="titles.loginWithPhone"
                            href={() => {
                                Object.trackAction('email_login_switch_to_phone');
                                navigation.goBack();
                            }}
                            style={{ marginTop: 16, alignSelf: 'center' }}
                        />
                    </Section>
                </KeyboardAvoidingView>
            </Page>
        );
    },
    {
        isFetching: { from: getIsUserFetching },
        isLoading: { from: getUserLoadingState },
        error: { from: getUserError },
    },
);
